import { useEffect, useRef, useState, useCallback } from "react";

export type CelebrationType = "six" | "four" | "wicket" | "caught" | "golden_duck";

export interface CelebrationEvent {
  type: CelebrationType;
  id: string;
}

interface Delivery {
  runs: number;
  isWicket: boolean;
  isLegal: boolean;
  dismissalType?: string | null;
  extraType?: string | null;
  batterId?: string;
}

interface Innings {
  deliveries?: Delivery[];
  battingTeam?: string;
}

interface MatchData {
  innings?: Innings[];
  currentInnings?: number;
  teamAPlayers?: any[];
  teamBPlayers?: any[];
}

function getDeliveryFingerprint(innings: Innings[], currentInnings: number): string {
  let total = 0;
  for (const inn of innings) {
    total += inn.deliveries?.length ?? 0;
  }
  const currentDels = innings[currentInnings]?.deliveries?.length ?? 0;
  return `${total}:${currentInnings}:${currentDels}`;
}

function detectCelebration(
  delivery: Delivery,
  innings: Innings,
  matchData: MatchData
): CelebrationType | null {
  if (!delivery) return null;

  if (delivery.isWicket) {
    const batterId = delivery.batterId;
    if (batterId && delivery.isLegal) {
      const battingTeam = innings.battingTeam;
      const players = battingTeam === "A"
        ? matchData.teamAPlayers
        : matchData.teamBPlayers;
      const batter = players?.find((p: any) => p.id === batterId);
      if (batter && batter.runs === 0 && batter.balls <= 1 && batter.isOut) {
        return "golden_duck";
      }
    }

    if (delivery.dismissalType === "Caught") {
      return "caught";
    }

    return "wicket";
  }

  const isBattingBoundary = !delivery.extraType || delivery.extraType === null;

  if (delivery.runs === 6 && isBattingBoundary && delivery.isLegal) {
    return "six";
  }

  if (delivery.runs === 4 && isBattingBoundary && delivery.isLegal) {
    return "four";
  }

  return null;
}

export function usePublicCelebration(matchData: MatchData | null | undefined) {
  const [queue, setQueue] = useState<CelebrationEvent[]>([]);
  const [current, setCurrent] = useState<CelebrationEvent | null>(null);
  const prevFingerprintRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const effectsEnabled = useEffectsEnabled();

  useEffect(() => {
    if (!matchData?.innings || !Array.isArray(matchData.innings)) {
      return;
    }

    const currentInnings = matchData.currentInnings ?? 0;
    const fingerprint = getDeliveryFingerprint(matchData.innings, currentInnings);

    if (!initializedRef.current) {
      initializedRef.current = true;
      prevFingerprintRef.current = fingerprint;
      return;
    }

    if (fingerprint === prevFingerprintRef.current) {
      return;
    }

    const prevFingerprint = prevFingerprintRef.current;
    prevFingerprintRef.current = fingerprint;

    if (!effectsEnabled) return;

    const prevTotal = prevFingerprint ? parseInt(prevFingerprint.split(":")[0], 10) : 0;
    const newTotal = parseInt(fingerprint.split(":")[0], 10);

    if (newTotal <= prevTotal) return;

    const inn = matchData.innings[currentInnings];
    if (!inn?.deliveries?.length) return;

    const latestDelivery = inn.deliveries[inn.deliveries.length - 1];
    const celebType = detectCelebration(latestDelivery, inn, matchData);

    if (celebType) {
      const event: CelebrationEvent = {
        type: celebType,
        id: `${celebType}-${Date.now()}-${newTotal}`,
      };
      setQueue((prev) => [...prev, event]);
    }
  }, [matchData, effectsEnabled]);

  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
    }
  }, [current, queue]);

  const dismiss = useCallback(() => {
    setCurrent(null);
  }, []);

  return { current, dismiss };
}

function useEffectsEnabled(): boolean {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem("watchwicket-public-effects") !== "off";
    } catch {
      return true;
    }
  });

  useEffect(() => {
    const handler = () => {
      try {
        setEnabled(localStorage.getItem("watchwicket-public-effects") !== "off");
      } catch { /* ignore */ }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return enabled;
}

export function useSoundEnabled(): [boolean, (v: boolean) => void] {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem("watchwicket-public-sound") !== "off";
    } catch {
      return true;
    }
  });

  const toggle = useCallback((v: boolean) => {
    setEnabled(v);
    try {
      localStorage.setItem("watchwicket-public-sound", v ? "on" : "off");
    } catch { /* ignore */ }
  }, []);

  return [enabled, toggle];
}

export function useEffectsToggle(): [boolean, (v: boolean) => void] {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem("watchwicket-public-effects") !== "off";
    } catch {
      return true;
    }
  });

  const toggle = useCallback((v: boolean) => {
    setEnabled(v);
    try {
      localStorage.setItem("watchwicket-public-effects", v ? "on" : "off");
    } catch { /* ignore */ }
  }, []);

  return [enabled, toggle];
}
